import React, { useEffect, useMemo, useState } from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@material-ui/core';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import ColoredButton from 'components/ColoredButton';
import type { RopaReviewStartSummary } from 'config/api/ropaReviews/ropaReviews.types';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';

type Props = {
  loading?: boolean;
  onClose: () => void;
  onConfirm: (payload: { deadline: string; reviewInstructions: string }) => void;
  open: boolean;
  summary?: RopaReviewStartSummary;
  summaryLoading?: boolean;
};

const StartRopaReviewModal: React.FC<Props> = ({ loading = false, onClose, onConfirm, open, summary, summaryLoading = false }) => {
  const { t } = useTranslation();
  const minDeadlineDate = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return format(tomorrow, 'yyyy-MM-dd');
  }, []);
  const [deadline, setDeadline] = useState('');
  const [instructions, setInstructions] = useState('');
  const [deadlineError, setDeadlineError] = useState('');
  const [instructionsError, setInstructionsError] = useState('');

  useEffect(() => {
    if (open) {
      setDeadline('');
      setInstructions('');
      setDeadlineError('');
      setInstructionsError('');
    }
  }, [open]);

  const handleClose = () => {
    setDeadlineError('');
    setInstructionsError('');
    onClose();
  };

  const handleConfirm = () => {
    let hasError = false;

    if (!deadline) {
      setDeadlineError(t(validation_messages.required));
      hasError = true;
    } else {
      const selectedDate = new Date(deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate <= today) {
        setDeadlineError(t(processing_activities_messages.asset_ropa.modals.start_review.deadline_future));
        hasError = true;
      }
    }

    if (!instructions.trim()) {
      setInstructionsError(t(validation_messages.required));
      hasError = true;
    }

    if (hasError) return;

    onConfirm({
      deadline,
      reviewInstructions: instructions.trim(),
    });
  };

  return (
    <Dialog fullWidth maxWidth='sm' onClose={handleClose} open={open}>
      <DialogTitle>{t(processing_activities_messages.asset_ropa.modals.start_review.title)}</DialogTitle>
      <DialogContent>
        <Typography color='textSecondary' gutterBottom variant='body2'>
          {t(processing_activities_messages.asset_ropa.modals.start_review.intro)}
        </Typography>
        <div>
          {summaryLoading ? (
            <Typography color='textSecondary' variant='body2'>
              {t(processing_activities_messages.asset_ropa.modals.start_review.summary_loading)}
            </Typography>
          ) : (
            <Typography className='' color='textSecondary' variant='body2'>
              {t(processing_activities_messages.asset_ropa.modals.start_review.summary, {
                assets: summary?.eligibleAssetsCount ?? 0,
                owners: summary?.uniqueOperationalOwnersCount ?? 0,
                activities: summary?.assetProcessingActivitiesCount ?? 0,
              })}
            </Typography>
          )}
        </div>
        <TextField
          error={Boolean(deadlineError)}
          fullWidth
          helperText={deadlineError}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: minDeadlineDate }}
          label={t(processing_activities_messages.asset_ropa.modals.start_review.deadline)}
          margin='normal'
          onChange={event => {
            setDeadline(event.target.value);
            if (deadlineError) setDeadlineError('');
          }}
          type='date'
          value={deadline}
          variant='outlined'
        />
        <TextField
          error={Boolean(instructionsError)}
          fullWidth
          helperText={instructionsError || t(processing_activities_messages.asset_ropa.modals.start_review.instructions_help)}
          label={t(processing_activities_messages.asset_ropa.modals.start_review.instructions)}
          margin='normal'
          multiline
          onChange={event => {
            setInstructions(event.target.value);
            if (instructionsError) setInstructionsError('');
          }}
          rows={5}
          value={instructions}
          variant='outlined'
        />
      </DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='grey'
          disabled={loading}
          icon={false}
          onClick={handleClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel)}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={loading || summaryLoading}
          icon={false}
          onClick={handleConfirm}
          showLoader={loading}
          textLike={false}
          variant='contained'
        >
          {t(processing_activities_messages.asset_ropa.modals.start_review.confirm)}
        </ColoredButton>
      </DialogActions>
    </Dialog>
  );
};

export default StartRopaReviewModal;
